import { MaterialResources } from "@/state/MaterialResources";
import { ModelInstance } from "../modelInstance/ModelInstance";
import { ElementInstance } from "../element/Element";
import { MeshInstance } from "../modelInstance/MeshInstance";
import { MaterialRenderData } from "./MaterialRenderData";
import { Color } from "three";

export const getMeshMaterialRenderData = (
    model: ModelInstance,
    instance: MeshInstance | ElementInstance,
    materials: Record<string, MaterialResources>
): MaterialRenderData[] => {
    if ("config" in instance && instance.config.isArea) {
        return [];
    }

    const meshMaterials =
        "raw" in instance
            ? instance.raw.materials
            : model.raw.meshes.find(
                  (mesh) => mesh.name === model.config.elements[instance.elementId].meshName
              )?.materials ?? [];

    if (instance.geometry.groups.length > 0) {
        return instance.geometry.groups.map((group) => {
            const materialIndex = group.materialIndex ?? 0;
            const materialDef = meshMaterials[materialIndex];

            const material = getMaterialRenderData(
                model,
                materialDef.name,
                materialDef.smoothing,
                materials
            );

            return material;
        });
    } else {
        const materialDef = meshMaterials[0];
        const material = getMaterialRenderData(
            model,
            materialDef.name,
            materialDef.smoothing,
            materials
        );

        return [material];
    }
};

const getMaterialRenderData = (
    model: ModelInstance,
    meshMaterialName: string,
    smoothing: boolean,
    materials: Record<string, MaterialResources>
): MaterialRenderData => {
    const slotId = model.config.materialMapping[meshMaterialName];
    const materialSlotConfig = model.materialSlotMapping[slotId];
    const rawMaterial = model.raw.materials.find(
        (material) => material.definition.name === meshMaterialName
    );
    const resources = materials[materialSlotConfig?.materialName] ?? {
        config: undefined,
        textures: rawMaterial?.textures ?? {},
    };

    let textures = resources.textures;
    if (materialSlotConfig?.ignoreTextureColor) {
        textures = {
            ...textures,
            color: undefined,
        };
    }

    return {
        textures,
        color: getColor(materialSlotConfig?.color ?? rawMaterial?.definition?.diffuseColor),
        emissiveColor: materialSlotConfig?.emissive,
        emissiveIntensity: materialSlotConfig?.emissiveIntensity,
        meshMaterialName,
        smoothing,
        transparency: rawMaterial?.definition?.transparency,
        refractiveIndex: rawMaterial?.definition?.refractiveIndex,
        roughness: rawMaterial?.definition?.roughness,
        metalness: rawMaterial?.definition?.metalness,
        clearcoatThickness: rawMaterial?.definition?.clearcoatThickness,
        clearcoatRoughness: rawMaterial?.definition?.clearcoatRoughness,
        anisotropy: rawMaterial?.definition?.anisotropy,
        anisotropyRotation: rawMaterial?.definition?.anisotropyRotation,
        bumpScale: rawMaterial?.definition?.bumpMultiplier,
    };
};

const getColor = (value: Color | [r: number, g: number, b: number] | undefined) => {
    if (value === undefined) {
        return undefined;
    }

    if (value instanceof Color) {
        return value;
    }

    return new Color(value[0], value[1], value[2]);
};
